import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { rendicionAnualService, TipoPresentacionRendicion } from '../../../services/api';
import type { RendicionAnual } from './types';

// =============================================================================
// Schema
// =============================================================================

const schema = z
  .object({
    tipoPresentacion: z.enum(['COMPLETA', 'COPIA_DISTRITO']),
    ejercicioNumero: z.coerce.number().int().positive('Debe ser mayor a 0'),
    fechaInicio: z.string().min(1, 'Requerida'),
    fechaCierre: z.string().min(1, 'Requerida'),
    codigoSpeca: z.string().optional(),
  })
  .refine((d) => new Date(d.fechaCierre) > new Date(d.fechaInicio), {
    message: 'Fecha de cierre debe ser posterior al inicio',
    path: ['fechaCierre'],
  })
  .refine(
    (d) => d.tipoPresentacion === 'COMPLETA' || (d.codigoSpeca && d.codigoSpeca.trim().length > 0),
    { message: 'Requerido para COPIA_DISTRITO', path: ['codigoSpeca'] },
  );

type FormData = z.infer<typeof schema>;

interface PartidoLite {
  id?: string;
  denominacion?: string;
  sigla?: string;
  tipo?: string;
  domicilioLegal?: string | null;
  localidad?: string | null;
  codigoPostal?: string | null;
}

interface Props {
  partidoId: string;
  partido: PartidoLite | null;
  rendicion: RendicionAnual | null;
  onCreated: (rendicion: RendicionAnual) => void;
  onNext: () => void;
  onChangeTipo: (tipo: TipoPresentacionRendicion) => void;
}

/**
 * Tipo de presentación obligatorio según tipo de partido.
 * Base: Ley XII-23 Art. 22 (partidos de distrito) + Art. 53 inc. d.
 */
function tipoPresentacionSegunPartido(tipoPartido: string | undefined): TipoPresentacionRendicion | null {
  if (!tipoPartido) return null;
  if (tipoPartido === 'DISTRITO') return 'COPIA_DISTRITO';
  // Sin 'CONFEDERACION': dejó de ser un `TipoPartido`. Una confederación llega acá con
  // su ámbito real (la migración de alianzas las dejó en PROVINCIAL), así que sigue
  // cayendo en COMPLETA por esa vía.
  if (['MUNICIPAL', 'PROVINCIAL'].includes(tipoPartido)) return 'COMPLETA';
  return null;
}

export default function Paso1Datos({
  partidoId,
  partido: partidoProp,
  rendicion,
  onCreated,
  onNext,
  onChangeTipo,
}: Props) {
  const queryClient = useQueryClient();

  const partido = rendicion?.partido ?? partidoProp;
  const tipoSugerido = tipoPresentacionSegunPartido(partido?.tipo);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      tipoPresentacion: rendicion?.tipoPresentacion ?? tipoSugerido ?? 'COMPLETA',
      ejercicioNumero: rendicion?.ejercicioNumero ?? new Date().getFullYear() - 1,
      fechaInicio: rendicion?.fechaInicio?.slice(0, 10) ?? `${new Date().getFullYear() - 1}-01-01`,
      fechaCierre: rendicion?.fechaCierre?.slice(0, 10) ?? `${new Date().getFullYear() - 1}-12-31`,
      codigoSpeca: rendicion?.codigoSpeca ?? '',
    },
  });

  // Si el tipo del partido llega tarde (query asíncrona) y todavía no hay rendición,
  // aplicamos el tipo sugerido.
  useEffect(() => {
    if (!rendicion && tipoSugerido) {
      setValue('tipoPresentacion', tipoSugerido);
      onChangeTipo(tipoSugerido);
    }
  }, [rendicion, tipoSugerido, setValue, onChangeTipo]);

  const tipoActual = watch('tipoPresentacion');
  const esCopiaDistrito = tipoActual === 'COPIA_DISTRITO';

  // Sincronizo el tipo actual con el shell (para que el stepper y la navegación
  // respeten la opción elegida aunque el usuario no haya guardado todavía).
  useEffect(() => {
    onChangeTipo(tipoActual);
  }, [tipoActual, onChangeTipo]);

  const saveMutation = useMutation({
    mutationFn: async (data: FormData) => {
      onChangeTipo(data.tipoPresentacion);
      const payload = {
        tipoPresentacion: data.tipoPresentacion,
        ejercicioNumero: data.ejercicioNumero,
        fechaInicio: data.fechaInicio,
        fechaCierre: data.fechaCierre,
        codigoSpeca: data.codigoSpeca || null,
        versionSpeca: null,
      };
      if (rendicion) {
        return rendicionAnualService.updatePaso1(rendicion.id, payload);
      }
      return rendicionAnualService.create(partidoId, payload);
    },
    onSuccess: (res) => {
      toast.success(rendicion ? 'Paso 1 actualizado' : 'Rendición creada');
      queryClient.invalidateQueries({ queryKey: ['rendiciones-partido', partidoId] });
      onCreated(res.data as RendicionAnual);
      onNext();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? 'No se pudo guardar');
    },
  });

  const onSubmit = (data: FormData) => {
    if (rendicion && !isDirty) {
      onNext();
      return;
    }
    saveMutation.mutate(data);
  };

  const esReadonlyEjercicio = !!rendicion;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6 space-y-5">
        <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-3">
          Datos del Ejercicio Contable
        </h2>

        <div className="bg-blue-50 border-l-4 border-blue-400 p-3 text-sm text-blue-900">
          Los partidos de distrito que ya presentaron ante la CNE a través de <strong>SPECA</strong> solo
          deben adjuntar copia. La carga completa aplica a <strong>partidos provinciales y municipales</strong>.
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Tipo de presentación
            </label>
            <select
              {...register('tipoPresentacion')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="COMPLETA">Carga completa (provincial / municipal)</option>
              <option value="COPIA_DISTRITO">Copia de presentación nacional (distrito)</option>
            </select>
            {tipoSugerido && tipoActual !== tipoSugerido && (
              <p className="mt-1 text-xs text-amber-700">
                Para partidos {partido?.tipo}, la presentación habitual es{' '}
                <strong>
                  {tipoSugerido === 'COPIA_DISTRITO'
                    ? 'Copia de presentación nacional'
                    : 'Carga completa'}
                </strong>
                .
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Agrupación política
            </label>
            <input
              type="text"
              readOnly
              value={partido ? `${partido.denominacion} (${partido.sigla})` : '— seleccionar —'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-600"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Ejercicio Económico N°
            </label>
            <input
              type="number"
              min={1}
              {...register('ejercicioNumero')}
              readOnly={esReadonlyEjercicio}
              className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                esReadonlyEjercicio ? 'bg-gray-100 text-gray-600' : ''
              }`}
            />
            {errors.ejercicioNumero && (
              <p className="mt-1 text-xs text-red-600">{errors.ejercicioNumero.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Fecha de inicio
            </label>
            <input
              type="date"
              {...register('fechaInicio')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.fechaInicio && <p className="mt-1 text-xs text-red-600">{errors.fechaInicio.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Fecha de cierre
            </label>
            <input
              type="date"
              {...register('fechaCierre')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.fechaCierre && <p className="mt-1 text-xs text-red-600">{errors.fechaCierre.message}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Domicilio legal
            </label>
            <input
              type="text"
              readOnly
              value={partido?.domicilioLegal ?? '—'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-600"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Localidad
            </label>
            <input
              type="text"
              readOnly
              value={partido?.localidad ?? '—'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-600"
            />
          </div>
        </div>

        {esCopiaDistrito && (
          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 space-y-4">
            <p className="text-sm text-amber-900">
              <strong>Copia de presentación nacional (Art. 53 inc. d, Ley XII-23).</strong> Los pasos 2 a 5
              se saltean. Cargá los datos SPECA acá y subí el PDF en el paso de <strong>Firma y envío</strong>.
            </p>
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
                Código de seguridad SPECA <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                {...register('codigoSpeca')}
                placeholder="Ej: a7f3b9c2e1d84056"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {errors.codigoSpeca && (
                <p className="mt-1 text-xs text-red-600">{errors.codigoSpeca.message}</p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="submit"
          disabled={saveMutation.isPending}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50 inline-flex items-center gap-2"
        >
          {saveMutation.isPending && (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          )}
          {saveMutation.isPending
            ? 'Guardando...'
            : !rendicion
            ? 'Crear rendición →'
            : isDirty
            ? 'Guardar y continuar →'
            : 'Continuar →'}
        </button>
      </div>
    </form>
  );
}
